'use client';
import useAuth from 'hooks/useAuth';
import { usePathname } from 'next/navigation';
import { createContext, Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { createCalendarCategory, deleteCalendarCategory, getCalendarCategoriesList, updateCalendarCategory } from 'services/calendar';
import { getCampaignsList } from 'services/campaign';
import {
  archiveCompanyContactSource,
  archiveCompanyContactStatus,
  archiveCompanyContactTag,
  archiveCompanyDocumentCategory,
  archiveCompanyLoanLender,
  archiveCompanyLoanType,
  archiveCompanyPartnerIndustry,
  archiveCompanyPartnerRoles,
  archiveCompanyPartnerSource,
  archiveCompanyPartnerStatus,
  archiveCompanyPartnerTags,
  CompanyUsersResponse,
  createCompanyContactSource,
  createCompanyContactStatus,
  createCompanyContactTag,
  createCompanyDocumentCategory,
  createCompanyLoanLender,
  createCompanyLoanType,
  createCompanyPartnerIndustry,
  createCompanyPartnerRole,
  createCompanyPartnerSource,
  createCompanyPartnerStatus,
  createCompanyPartnerTags,
  getCompanyContactSources,
  getCompanyContactStatus,
  getCompanyContactTags,
  getCompanyDocumentCategories,
  getCompanyLoanLenders,
  getCompanyLoanTypes,
  getCompanyPartnerIndustry,
  getCompanyPartnerRoles,
  getCompanyPartnerSources,
  getCompanyPartnerStatus,
  getCompanyPartnerTags,
  getCompanyUsers,
  updateCompanyContactSourceById,
  updateCompanyContactStatusById,
  updateCompanyContactTagById,
  updateCompanyDocumentCategoryById,
  updateCompanyLoanLenderById,
  updateCompanyLoanTypeById,
  updateCompanyPartnerIndustryById,
  updateCompanyPartnerRoleById,
  updateCompanyPartnerSourceById,
  updateCompanyPartnerStatusById,
  updateCompanyPartnerTagsById
} from 'services/company';
import { createUser, getNotifications, getUsersList } from 'services/user';
import { CampaignList, CampaignStatusEnum, CampaignTargetEnum } from 'types/campaign';
import { CustomField } from 'types/company-settings';
import { PageMetaData } from 'types/pagination';
import { User, UserForm } from 'types/user';
import { ENotificationsType, Notification, NotificationTab } from 'types/user-settings';

export type QueryParam = Record<string, any>;

const initialPageData: PageMetaData = {
  page: 0,
  take: 50,
  itemCount: 50,
  pageCount: 1,
  hasPreviousPage: false,
  hasNextPage: false
};

export type SettingsContextProps = {
  companyUsers: User[];
  queryParams: QueryParam;
  pageData: PageMetaData;
  customFields: CustomField[];
  usersList: User[];
  sourcesList: CustomField[];
  statusList: CustomField[];
  partnerStatusList: CustomField[];
  partnerIndustryList: CustomField[];
  partnerSourcesList: CustomField[];
  tagsList: CustomField[];
  partnerTagsList: CustomField[];
  loanTypesList: CustomField[];
  loanLendersList: CustomField[];
  campaignList: CampaignList[];
  notificationsList: Notification[];
  notificationsCount: number;
  currentNotificationsTab: NotificationTab;
  currentOnboardingStepStore: number;
  currentOnboardingStepLabelStore: string;
  onboardingStepsTotalStore: number;
  partnerSwitch: boolean;
  isPartners: boolean;
  setCompanyUsers: Dispatch<SetStateAction<User[]>>;
  setQueryParams: Dispatch<SetStateAction<QueryParam>>;
  setPageData: Dispatch<SetStateAction<PageMetaData>>;
  setCustomFields: Dispatch<SetStateAction<CustomField[]>>;
  setNotificationsList: Dispatch<SetStateAction<Notification[]>>;
  setCurrentNotificationsTab: Dispatch<SetStateAction<NotificationTab>>;
  setNotificationsCount: Dispatch<SetStateAction<number>>;
  setCurrentOnboardingStepStore: Dispatch<SetStateAction<number>>;
  setCurrentOnboardingStepLabelStore: Dispatch<SetStateAction<string>>;
  setOnboardingStepsTotalStore: Dispatch<SetStateAction<number>>;
  listUserSelectOptions: () => Promise<void>;
  listCompanyUsers: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CompanyUsersResponse>;
  addCompanyUser: (user: UserForm) => Promise<User>;
  listCompanyDocumentCategories: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyContactSources: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyContactStatus: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyLoanTypes: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyLoanLenders: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyContactTags: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  archiveCompanyCustomField: (customFieldType: string, customFieldId: string, subId?: string) => Promise<void>;
  addCompanyCustomField: (customFieldType: string, customField: Partial<CustomField>) => Promise<CustomField>;
  updateCompanyCustomFields: (customFieldType: string, customFieldId: string, customField: Partial<CustomField>) => Promise<void>;
  listNotifications: (notificationTab?: NotificationTab, headers?: Record<string, any>) => Promise<Notification[]>;
  setPartnerSwitch: Dispatch<SetStateAction<boolean>>;
  listCompanyPartnerTags: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyPartnerRole: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyPartnerIndustry: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyPartnerStatus: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyPartnerSources: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
  listCompanyCalendarCategories: (queryParams?: Record<string, any>, headers?: Record<string, any>) => Promise<CustomField[]>;
};

const SettingsContext = createContext({} as SettingsContextProps);

type SettingsProviderProps = {
  children: ReactNode;
};

function SettingsProvider({ children }: SettingsProviderProps) {
  const { company } = useAuth();

  const [queryParams, setQueryParams] = useState<QueryParam>({
    page: 0,
    take: 50,
    orderBy: 'firstName',
    order: 'ASC'
  });
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [pageData, setPageData] = useState(initialPageData);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [usersList, setUsersList] = useState<User[]>([]);
  const [sourcesList, setSourcesList] = useState<CustomField[]>([]);
  const [statusList, setStatusList] = useState<CustomField[]>([]);
  const [partnerStatusList, setPartnerStatusList] = useState<CustomField[]>([]);
  const [tagsList, setTagsList] = useState<CustomField[]>([]);
  const [partnerTagsList, setPartnerTagsList] = useState<CustomField[]>([]);
  const [partnerIndustryList, setPartnerIndustryList] = useState<CustomField[]>([]);
  const [partnerSourcesList, setPartnerSourcesList] = useState<CustomField[]>([]);
  const [loanTypesList, setLoanTypesList] = useState<CustomField[]>([]);
  const [loanLendersList, setLoanLendersList] = useState<CustomField[]>([]);
  const [campaignList, setCampaignList] = useState<CampaignList[]>([]);
  const [notificationsList, setNotificationsList] = useState<Notification[]>([]);
  const [currentNotificationsTab, setCurrentNotificationsTab] = useState<NotificationTab>('unread');
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [currentOnboardingStepStore, setCurrentOnboardingStepStore] = useState(0);
  const [currentOnboardingStepLabelStore, setCurrentOnboardingStepLabelStore] = useState('');
  const [onboardingStepsTotalStore, setOnboardingStepsTotalStore] = useState(9);

  const [partnerSwitch, setPartnerSwitch] = useState(false);

  const pathname = usePathname();

  const isPartners = pathname.includes('partners');

  const listUserSelectOptions = async () => {
    try {
      const [
        usersList,
        sourcesList,
        statusList,
        partnerStatusList,
        tagsList,
        partnerTagsList,
        loanTypesList,
        loanLendersList,
        partnerIndustryList,
        partnerSourcesList,
        campaignList
      ] = await Promise.all([
        getUsersList(),
        getCompanyContactSources(),
        getCompanyContactStatus(),
        getCompanyPartnerStatus(),
        getCompanyContactTags(),
        getCompanyPartnerTags(),
        getCompanyLoanTypes(),
        getCompanyLoanLenders(),
        listCompanyPartnerIndustry(),
        getCompanyPartnerSources(),
        company
          ? getCampaignsList(company?.id, {
              status: CampaignStatusEnum.ACTIVE,
              target: isPartners ? CampaignTargetEnum.PARTNER : CampaignTargetEnum.CONTACT
            })
          : Promise.resolve([])
      ]);

      setUsersList(usersList || []);
      setSourcesList(sourcesList || []);
      setStatusList(statusList || []);
      setPartnerStatusList(partnerStatusList || []);
      setTagsList(tagsList || []);
      setPartnerTagsList(partnerTagsList || []);
      setLoanTypesList(loanTypesList || []);
      setLoanLendersList(loanLendersList || []);
      setCampaignList(campaignList || []);
      setPartnerIndustryList(partnerIndustryList || []);
      setPartnerSourcesList(partnerSourcesList || []);
    } catch (error) {
      console.error('There was an error getting select options:', error);
      throw error;
    }
  };

  const addCompanyUser = async (user: UserForm) => {
    try {
      const response = await createUser(user);
      const companyUsers = await listCompanyUsers(queryParams);
      setCompanyUsers(companyUsers.data);
      setPageData(companyUsers.meta);
      return response;
    } catch (error) {
      console.error('There was an error creating user:', error);
      throw error;
    }
  };

  const listCompanyUsers = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyUsers(queryParams, headers);
      setCompanyUsers(response.data);
      setPageData(response.meta);
      return response;
    } catch (error) {
      console.error('There was an error listing company users:', error);
      throw error;
    }
  };

  const listCompanyDocumentCategories = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyDocumentCategories(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing document categories:', error);
      throw error;
    }
  };

  const listCompanyLoanTypes = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyLoanTypes(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing loan types:', error);
      throw error;
    }
  };

  const updateCompanyLoanLender = async (customFieldId: string, customField: Partial<CustomField>) => {
    try {
      const response = await updateCompanyLoanLenderById(customFieldId, customField);
      const customFields = await listCompanyLoanLenders(queryParams);
      setCustomFields(customFields);
      return response;
    } catch (error) {
      console.error('There was an error updating custom field:', error);
      throw error;
    }
  };

  const listCompanyLoanLenders = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyLoanLenders(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing loan lenders:', error);
      throw error;
    }
  };

  const listCompanyContactTags = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyContactTags(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing contact tags:', error);
      throw error;
    }
  };

  const listCompanyPartnerTags = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyPartnerTags(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing parter tags:', error);
      throw error;
    }
  };

  const listCompanyPartnerRole = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyPartnerRoles(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing parter roles:', error);
      throw error;
    }
  };

  const listCompanyPartnerIndustry = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyPartnerIndustry(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing parter industries:', error);
      throw error;
    }
  };

  const listCompanyCalendarCategories = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCalendarCategoriesList(queryParams, headers);

      const customFields: CustomField[] = response.map((category) => ({
        active: true,
        companyId: category.company?.id ?? '',
        id: category.id,
        name: category.name,
        color: category.color
      }));

      setCustomFields(customFields);
      return customFields;
    } catch (error) {
      console.error('There was an error listing parter industries:', error);
      throw error;
    }
  };

  const listCompanyPartnerStatus = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyPartnerStatus(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing parter statuses:', error);
      throw error;
    }
  };

  const listCompanyPartnerSources = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyPartnerSources(queryParams, headers);
      setCustomFields(response);
      setPartnerSourcesList(response);
      return response;
    } catch (error) {
      console.error('There was an error listing parter sources:', error);
      throw error;
    }
  };

  const addCompanyCustomField = async (customFieldType: string, customField: Partial<CustomField>): Promise<CustomField> => {
    try {
      let newCustomField: CustomField;
      let customFields: CustomField[] = [];

      if (customFieldType !== 'calendar-categories') {
        delete customField.color;
      }

      switch (customFieldType) {
        case 'contact-source':
          newCustomField = await createCompanyContactSource(customField);
          customFields = await listCompanyContactSources(queryParams);
          break;
        case 'contact-status':
          newCustomField = await createCompanyContactStatus(customField);
          customFields = await listCompanyContactStatus(queryParams);
          break;
        case 'contact-tags':
          newCustomField = await createCompanyContactTag(customField);
          customFields = await listCompanyContactTags(queryParams);
          break;
        case 'document-category':
          newCustomField = await createCompanyDocumentCategory(customField);
          customFields = await listCompanyDocumentCategories(queryParams);
          break;
        case 'loan-type':
          newCustomField = await createCompanyLoanType(customField);
          customFields = await listCompanyLoanTypes(queryParams);
          break;
        case 'loan-lender':
          newCustomField = await createCompanyLoanLender(customField);
          customFields = await listCompanyLoanLenders(queryParams);
          break;
        case 'partner-source':
          newCustomField = await createCompanyPartnerSource(customField);
          customFields = await listCompanyPartnerSources(queryParams);
          break;
        case 'partner-status':
          newCustomField = await createCompanyPartnerStatus(customField);
          customFields = await listCompanyPartnerStatus(queryParams);
          break;
        case 'partner-industry':
          newCustomField = await createCompanyPartnerIndustry(customField);
          customFields = await listCompanyPartnerIndustry(queryParams);
          break;
        case 'partner-role':
          newCustomField = await createCompanyPartnerRole(customField);
          customFields = await listCompanyPartnerRole(queryParams);
          break;
        case 'partner-tags':
          newCustomField = await createCompanyPartnerTags(customField);
          customFields = await listCompanyPartnerTags(queryParams);
          break;
        case 'calendar-categories':
          newCustomField = await createCalendarCategory(customField.name as string, customField.color as string);
          customFields = await listCompanyCalendarCategories(queryParams);
          break;
        default:
          newCustomField = await createCompanyContactSource(customField);
          customFields = await listCompanyContactSources(queryParams);
      }

      setCustomFields(customFields);
      return newCustomField;
    } catch (error) {
      console.error('There was an error creating custom field:', error);
      throw error;
    }
  };

  const listCompanyContactStatus = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyContactStatus(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing contact tags:', error);
      throw error;
    }
  };

  const archiveCompanyCustomField = async (customFieldType: string, customFieldId: string, subId?: string) => {
    let customFields: CustomField[] = [];
    try {
      switch (customFieldType) {
        case 'contact-source':
          await archiveCompanyContactSource(customFieldId, subId);
          customFields = await listCompanyContactSources(queryParams);
          break;
        case 'contact-status':
          await archiveCompanyContactStatus(customFieldId, subId);
          customFields = await listCompanyContactStatus(queryParams);
          break;
        case 'contact-tags':
          await archiveCompanyContactTag(customFieldId, subId);
          customFields = await listCompanyContactTags(queryParams);
          break;
        case 'document-category':
          await archiveCompanyDocumentCategory(customFieldId, subId);
          customFields = await listCompanyDocumentCategories(queryParams);
          break;
        case 'loan-type':
          await archiveCompanyLoanType(customFieldId, subId);
          customFields = await listCompanyLoanTypes(queryParams);
          break;
        case 'loan-lender':
          await archiveCompanyLoanLender(customFieldId, subId);
          customFields = await listCompanyLoanLenders(queryParams);
          break;
        case 'partner-source':
          await archiveCompanyPartnerSource(customFieldId, subId);
          customFields = await listCompanyPartnerSources(queryParams);
          break;
        case 'partner-status':
          await archiveCompanyPartnerStatus(customFieldId, subId);
          customFields = await listCompanyPartnerStatus(queryParams);
          break;
        case 'partner-industry':
          await archiveCompanyPartnerIndustry(customFieldId, subId);
          customFields = await listCompanyPartnerIndustry(queryParams);
          break;
        case 'partner-role':
          await archiveCompanyPartnerRoles(customFieldId, subId);
          customFields = await listCompanyPartnerRole(queryParams);
          break;
        case 'partner-tags':
          await archiveCompanyPartnerTags(customFieldId, subId);
          customFields = await listCompanyPartnerTags(queryParams);
          break;
        case 'calendar-categories':
          await deleteCalendarCategory(customFieldId);
          customFields = await listCompanyCalendarCategories(queryParams);
          break;
        default:
          await archiveCompanyContactSource(customFieldId, subId);
          customFields = await listCompanyContactSources(queryParams);
      }

      setCustomFields(customFields);
    } catch (error) {
      console.error('There was an error updating custom field:', error);
      throw error;
    }
  };

  const updateCompanyCustomFields = async (customFieldType: string, customFieldId: string, customField: Partial<CustomField>) => {
    let customFields: CustomField[] = [];

    if (customFieldType !== 'calendar-categories') {
      delete customField.color;
    }

    try {
      switch (customFieldType) {
        case 'contact-source':
          await updateCompanyContactSourceById(customFieldId, customField);
          customFields = await listCompanyContactSources(queryParams);
          break;
        case 'contact-status':
          await updateCompanyContactStatusById(customFieldId, customField);
          customFields = await listCompanyContactStatus(queryParams);
          break;
        case 'contact-tags':
          await updateCompanyContactTagById(customFieldId, customField);
          customFields = await listCompanyContactTags(queryParams);
          break;
        case 'document-category':
          await updateCompanyDocumentCategoryById(customFieldId, customField);
          customFields = await listCompanyDocumentCategories(queryParams);
          break;
        case 'loan-type':
          await updateCompanyLoanTypeById(customFieldId, customField);
          customFields = await listCompanyLoanTypes(queryParams);
          break;
        case 'loan-lender':
          await updateCompanyLoanLender(customFieldId, customField);
          customFields = await listCompanyLoanLenders(queryParams);
          break;
        case 'partner-source':
          await updateCompanyPartnerSourceById(customFieldId, customField);
          customFields = await listCompanyPartnerSources(queryParams);
          break;
        case 'partner-status':
          await updateCompanyPartnerStatusById(customFieldId, customField);
          customFields = await listCompanyPartnerStatus(queryParams);
          break;
        case 'partner-industry':
          await updateCompanyPartnerIndustryById(customFieldId, customField);
          customFields = await listCompanyPartnerIndustry(queryParams);
          break;
        case 'partner-role':
          await updateCompanyPartnerRoleById(customFieldId, customField);
          customFields = await listCompanyPartnerRole(queryParams);
          break;
        case 'partner-tags':
          await updateCompanyPartnerTagsById(customFieldId, customField);
          customFields = await listCompanyPartnerTags(queryParams);
          break;
        case 'calendar-categories':
          await updateCalendarCategory(customFieldId, { name: customField.name, color: customField.color });
          customFields = await listCompanyCalendarCategories(queryParams);
          break;
        default:
          await updateCompanyContactSourceById(customFieldId, customField);
          customFields = await listCompanyContactSources(queryParams);
      }
      setCustomFields(customFields);
    } catch (error) {
      console.error('There was an error updating custom field:', error);
      throw error;
    }
  };

  const listCompanyContactSources = async (queryParams: Record<string, any> = {}, headers: Record<string, any> = {}) => {
    try {
      const response = await getCompanyContactSources(queryParams, headers);
      setCustomFields(response);
      return response;
    } catch (error) {
      console.error('There was an error listing contact sources:', error);
      throw error;
    }
  };

  /**
   * Checks if the 'next' creation time is within 15 minutes of the 'current' creation time.
   * @param nextCreatedAt - The createdAt timestamp of the next object (string or Date).
   * @param currentCreatedAt - The createdAt timestamp of the current object (string or Date).
   * @returns True if 'next' was created within 15 minutes of 'current', otherwise false.
   */
  function isCreatedWithinFifteenMinutes(nextCreatedAt: string | Date, currentCreatedAt: string | Date): boolean {
    const nextCreatedAtTime = new Date(nextCreatedAt).getTime();
    const currentCreatedAtTime = new Date(currentCreatedAt).getTime();

    const fifteenMinutesAfterCurrent = currentCreatedAtTime + 900000;

    return nextCreatedAtTime <= fifteenMinutesAfterCurrent;
  }

  const combineNotifications = (notifications: Notification[]): Notification[] => {
    if (notifications.length === 0) return [];

    notifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const combined: Notification[] = [];
    let current = notifications[0];

    for (let i = 1; i < notifications.length; i++) {
      const next = notifications[i];

      if (
        shouldAcumulateNotifications(current.type) &&
        next.userId === current.userId &&
        next.type === current.type &&
        next.read === current.read &&
        next.sent === current.sent &&
        next.title.includes(current.title) &&
        isCreatedWithinFifteenMinutes(next.createdAt, current.createdAt) &&
        next.action.values.ticketId === current.action.values.ticketId
      ) {
        current.quantity = (current.quantity || 1) + 1;
        current.childNotificationIds = [...(current.childNotificationIds || []), next.id];
      } else {
        combined.push(current);
        current = next;
      }
    }

    combined.push(current);

    return combined;
  };

  function shouldAcumulateNotifications(type: ENotificationsType): boolean {
    const typesThatSHouldBeAcumulated = [
      ENotificationsType.INCOMING_CALL,
      ENotificationsType.MESSAGES_REPLIES,
      ENotificationsType.INTEGRATION_REQUEST_UPDATED,
      ENotificationsType.JOURNEY_MESSAGES_PENDING,
      ENotificationsType.INSUFFICIENT_BALANCE,
      ENotificationsType.LOW_BALANCE
    ];
    return typesThatSHouldBeAcumulated.includes(type);
  }

  const listNotifications = async (notificationTab: NotificationTab = 'all', headers: Record<string, any> = {}) => {
    try {
      let notifications: Notification[] = [];
      switch (notificationTab) {
        case 'all':
          notifications = await getNotifications(headers);
          break;
        case 'unread':
          notifications = await getNotifications({ read: false }, headers);
          break;
        default:
          notifications = await getNotifications(headers);
      }

      const combinedNotifications = combineNotifications(notifications);

      setNotificationsList(combinedNotifications);

      return notifications;
    } catch (error) {
      console.error('There was an error listing notifications:', error);
      throw error;
    }
  };

  const values = {
    pageData,
    companyUsers,
    queryParams,
    customFields,
    usersList,
    sourcesList,
    statusList,
    partnerStatusList,
    partnerIndustryList,
    partnerSourcesList,
    tagsList,
    partnerTagsList,
    loanTypesList,
    loanLendersList,
    campaignList,
    notificationsList,
    currentNotificationsTab,
    notificationsCount,
    currentOnboardingStepStore,
    onboardingStepsTotalStore,
    currentOnboardingStepLabelStore,
    partnerSwitch,
    isPartners,
    setCompanyUsers,
    setQueryParams,
    setPageData,
    setCustomFields,
    setNotificationsList,
    setCurrentNotificationsTab,
    setNotificationsCount,
    setOnboardingStepsTotalStore,
    setCurrentOnboardingStepStore,
    setCurrentOnboardingStepLabelStore,
    listCompanyUsers,
    addCompanyUser,
    listUserSelectOptions,
    listCompanyDocumentCategories,
    listCompanyContactSources,
    listCompanyLoanTypes,
    listCompanyLoanLenders,
    listCompanyContactTags,
    listCompanyContactStatus,
    addCompanyCustomField,
    updateCompanyCustomFields,
    archiveCompanyCustomField,
    listNotifications,
    setPartnerSwitch,
    listCompanyPartnerTags,
    listCompanyPartnerRole,
    listCompanyPartnerIndustry,
    listCompanyPartnerStatus,
    listCompanyPartnerSources,
    listCompanyCalendarCategories
  };

  return <SettingsContext.Provider value={values}>{children}</SettingsContext.Provider>;
}

export { SettingsProvider, SettingsContext };
